import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { Play, Pause } from "lucide-react";

interface AudioPlayerProps {
  src: string | any;
}

// Clean voice-note player matching the Inbox reference: a solid primary circle
// play button, a compact primary waveform, and a single duration read-out.
const AudioPlayer = ({ src }: AudioPlayerProps) => {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const fmt = (seconds: number) => {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!waveformRef.current) return;

    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#c1c9e6",
      progressColor: "#0058c3",
      cursorWidth: 0,
      height: 28,
      barWidth: 2.5,
      barGap: 2,
      barRadius: 4,
    });

    ws.load(src);
    wavesurferRef.current = ws;

    ws.on("audioprocess", () => setCurrentTime(ws.getCurrentTime()));
    ws.on("ready", () => { setDuration(ws.getDuration()); setCurrentTime(0); });
    ws.on("play", () => setIsPlaying(true));
    ws.on("pause", () => setIsPlaying(false));
    ws.on("finish", () => { setIsPlaying(false); setCurrentTime(0); });

    return () => { ws.destroy(); };
  }, [src]);

  const togglePlay = () => wavesurferRef.current?.playPause();

  return (
    <div className="flex items-center gap-3 bg-white border border-outline-variant/30 rounded-xl shadow-sm p-2 pr-3 w-full">
      <button
        onClick={togglePlay}
        className="w-10 h-10 shrink-0 rounded-full bg-primary text-white grid place-items-center hover:brightness-110 transition"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
      </button>

      <div ref={waveformRef} className="flex-1 min-w-0" />

      <span className="text-[11px] text-outline font-medium tabular-nums shrink-0">
        {fmt(isPlaying ? currentTime : duration)}
      </span>
    </div>
  );
};

export default AudioPlayer;
